import classNames from 'classnames';
import React, { MutableRefObject, useEffect } from 'react';
import { type Swiper as SwiperType } from 'swiper';
import { Mousewheel } from 'swiper/modules';
import { Swiper } from 'swiper/react';
import Button from '@/components/molecules/button/button';
import { useI18n } from '@/libraries/contexts/i18n-context';
import useSwiperState from '@/libraries/hooks/use-swiper-state';
import styles from './swipe-items.module.scss';

export interface SwipeItemsProps {
  /** The children to render */
  children?: React.ReactNode;
  /** The class name to apply */
  className?: string;
  /** The swiper ref */
  swiperRef: MutableRefObject<SwiperType | undefined>;
}

// Room kept for the fade and buttons at the edges when a focused item is
// scrolled into view, matches the width of the edge gradients
const FOCUS_EDGE_OFFSET = 88;
const FOCUS_SCROLL_SPEED = 300;

const SwipeItems = ({ children, className, swiperRef }: SwipeItemsProps) => {
  const { t } = useI18n();
  const { isEnd, isBeginning, isOverflowing } = useSwiperState(swiperRef);

  // Keyboard users don't use the previous/next buttons, so an item that gets
  // focus is scrolled into view, just far enough to clear the edge fade. The
  // swiper clips its overflow, so the browser can't do this itself
  useEffect(() => {
    const swiper = swiperRef.current;

    if (!swiper?.el) return;

    const handleFocusIn = (event: FocusEvent) => {
      if (!(event.target instanceof Element)) return;

      const slide = event.target.closest('.swiper-slide');

      if (!slide || !swiper.el.contains(slide)) return;

      const containerRect = swiper.el.getBoundingClientRect();
      const slideRect = slide.getBoundingClientRect();
      const leftLimit = containerRect.left + FOCUS_EDGE_OFFSET;
      const rightLimit = containerRect.right - FOCUS_EDGE_OFFSET;
      let distance = 0;

      if (slideRect.left < leftLimit) {
        distance = leftLimit - slideRect.left;
      } else if (slideRect.right > rightLimit) {
        distance = rightLimit - slideRect.right;
      }

      if (!distance) return;

      const prefersReducedMotion = window.matchMedia(
        '(prefers-reduced-motion: reduce)',
      ).matches;

      // Kept within the swiper's bounds, so the ends never overshoot
      swiper.translateTo(
        swiper.translate + distance,
        prefersReducedMotion ? 0 : FOCUS_SCROLL_SPEED,
        true,
        true,
      );

      // translateTo only moves the items, so the active slide is synced too,
      // or the previous/next buttons would move on from the old position.
      // updateActiveIndex exists on the swiper but is missing from its types
      (
        swiper as SwiperType & { updateActiveIndex: () => void }
      ).updateActiveIndex();
      swiper.updateSlidesClasses();
    };

    swiper.el.addEventListener('focusin', handleFocusIn);

    // The items are measured before web fonts load, which makes them wider, so
    // the last item couldn't be scrolled fully into view. Measure again
    document.fonts?.ready.then(() => {
      if (!swiper.destroyed) swiper.update();
    });

    return () => {
      swiper.el?.removeEventListener('focusin', handleFocusIn);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [swiperRef.current]);

  return (
    <Swiper
      className={classNames(styles.component, className, {
        [styles.hasPrevious]: !isBeginning && isOverflowing,
        [styles.hasNext]: !isEnd && isOverflowing,
      })}
      modules={[Mousewheel]}
      mousewheel
      onSwiper={swiper => {
        swiperRef.current = swiper;
      }}
      slidesPerView="auto"
      wrapperClass={styles.wrapper}
    >
      {!isBeginning && isOverflowing && (
        // Pointer-only, keyboard focus scrolls the items into view instead
        <Button
          aria-hidden="true"
          aria-label={t('items.common.words.previous')}
          className={styles.slidePrevious}
          icon="ChevronLeft"
          onClick={() => swiperRef.current?.slidePrev()}
          tabIndex={-1}
          variant="tertiary"
        />
      )}

      {children && children}

      {!isEnd && isOverflowing && (
        <Button
          aria-hidden="true"
          aria-label={t('items.common.words.next')}
          className={styles.slideNext}
          icon="ChevronRight"
          onClick={() => swiperRef.current?.slideNext()}
          tabIndex={-1}
          variant="tertiary"
        />
      )}
    </Swiper>
  );
};

SwipeItems.displayName = 'SwipeItems';

export default SwipeItems;
